import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
    selectAddUserStepperSelectedAccess,
    selectAddUserStepperSelectedAccessLevel,
    selectAddUserStepperSelectedAccounts,
    selectAddUserStepperSelectedUser,
    selectAddUserStepperSelectedUserType,
} from '../../../store/selectors/grant-revoke-permissions.selector';
import { AdobePageName } from '../../../services/adobe-launch/adobe-launch.pages';
import { map, Observable, Subscription, take } from 'rxjs';
import {
    AdobeAnalyticsCallToAction,
    AnalyticsCtaType,
    CtaCallUsLinkLocations,
} from '../../../services/adobe-launch/adobe-launch.cta';
import {
    AdobeAnalyticsProcessStep,
    AdobeAnalyticsProcessType,
} from '../../../services/adobe-launch/adobe-launch.process';
import { AdobeAnalyticsEvent } from '../../../services/adobe-launch/adobe-launch.events';
import { environment } from '../../../../environments/environment';
import { Store } from '@ngrx/store';
import { AdobeLaunchService } from '../../../services/adobe-launch/adobe-launch.service';
import { BreakpointObserverService } from '../../../services/breakpoint-observer/breakpoint-observer.service';
import { ZciCookieService } from '../../../services/zci-cookie/zci-cookie.service';
import { AccessTypeSelection } from '../../../../common/enums/access-type-selection.enum';
import { ContentUtil } from '../../../utils/content/content.util';
import { UserDetailsDto } from '../../../../common/dtos/user-details.dto';
import { CONSTANTS, CONTENT_PLACEHOLDERS } from '../../../../common/constants/constants';
import { resetGrantRevokePermissionState } from '../../../store/actions/grant-revoke-permissions.action';
import { UserTypeSelection } from '../../../enums/user-type-selection.enum';
import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import { Router, RouterLink } from '@angular/router';
import { AdobeTagging } from '../../../utils/decorators/dev/adobe-launch';
import { adobeConfigData } from './gaf-confirmation-page-mvp-adobe';
import ContentFormatUtil from '../../../utils/content/content-format.util';
import { AsyncPipe, NgIf } from '@angular/common';
import { BannerModule } from '@vg-constellation/angular-20/banner';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import { IconModule } from '@vg-constellation/angular-20/icon';
import { ReplaceParamPipe } from '../../../pipes/replace-param/replace-param.pipe';
import { GafMvpPinComponent } from '../../stepper-steps/grant-access-flow-mvp/gaf-mvp-pin-component/gaf-mvp-pin-component';
import { OwnAccountDTO } from 'src/common/dtos/own-account.dto';

@AdobeTagging(adobeConfigData)
@Component({
    selector: 'zci-gaf-confirmation-page-mvp-component',
    templateUrl: 'gaf-confirmation-page-component-mvp.html',
    styleUrls: ['/gaf-confirmation-page-component-mvp.scss'],
    imports: [
        AsyncPipe,
        NgIf,
        RouterLink,
        ButtonModule,
        IconModule,
        BannerModule,
        ReplaceParamPipe,
        GafMvpPinComponent,
    ],
})
export class GafConfirmationPageMvpComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    protected readonly adobeLaunchService = inject(AdobeLaunchService);
    private readonly breakpointObserverService = inject(BreakpointObserverService);
    private readonly zciCookieService = inject(ZciCookieService);
    private readonly router = inject(Router);

    accountId: string;

    readonly accessTypeSelection = AccessTypeSelection;

    accessLevel$: Observable<string> = this.store.select(selectAddUserStepperSelectedAccessLevel);
    selectedUserType$: Observable<UserTypeSelection> = this.store.select(
        selectAddUserStepperSelectedUserType,
    );
    existingUser$: string = 'EXISTING';
    newUser$: string = 'NEW';
    selectedAccess$: Observable<AccessTypeSelection> = this.store.select(
        selectAddUserStepperSelectedAccess,
    );

    forceSurveyZeroHeight: boolean = true;

    @HostListener('window:MDigital_Form_Displayed')
    removeForcedHeightZero() {
        this.forceSurveyZeroHeight = false;
    }

    removeHTMLtags = ContentFormatUtil.removeHTMLtags;
    content = {
        ...ContentUtil.grantorFragmentContent.add_user.frmcAddUserReviewSubmit,
        scssInformationAccessRequestSubmitted: {
            ...ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.scssInformationAccessRequestSubmitted,
            text1: this.removeHTMLtags(ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.scssInformationAccessRequestSubmitted.text1),
        },
        lstExistingLimitedSuccessMessage: {
            ...ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstExistingLimitedSuccessMessage,
            listiWhatHappensNextSendMessage: this.removeHTMLtags(ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstExistingLimitedSuccessMessage.listiWhatHappensNextSendMessage),
            listiWhatHappensNextEsign: this.removeHTMLtags(ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstExistingLimitedSuccessMessage.listiWhatHappensNextEsign),
            listiWhatHappensNextAccessAccount: {
                ...ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstExistingLimitedSuccessMessage.listiWhatHappensNextAccessAccount,
                text1: this.removeHTMLtags(ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstExistingLimitedSuccessMessage.listiWhatHappensNextAccessAccount.text1.split('${nav-access-permissions}')[0]),
                text2: this.removeHTMLtags(ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstExistingLimitedSuccessMessage.listiWhatHappensNextAccessAccount.text1.split('${nav-access-permissions}')[1]),
            },
        },
        lstNewGranteeNotOnListSuccessMessage: {
            ...ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstNewGranteeNotOnListSuccessMessage,
            listiWhatsNextEmailLink: this.removeHTMLtags(ContentUtil.content.addUserFlow.mvp.reviewAndSubmit.lstNewGranteeNotOnListSuccessMessage.listiWhatsNextEmailLink),
        }
    };

    addUserAccessNamePlaceHolder: string = CONTENT_PLACEHOLDERS.FULL_NAME;

    addUserAccessFirstNamePlaceHolder: string = CONTENT_PLACEHOLDERS.FIRST_NAME;

    selectedUser$: Observable<UserDetailsDto>;

    isMobileView$: Observable<boolean> = this.breakpointObserverService.isMobileView$;

    subs = new Subscription();

    accessType: AccessTypeSelection;

    accountBalancesUrl: string;

    adobeTrackActionLocationInfoOnly: CtaCallUsLinkLocations =
        CtaCallUsLinkLocations.ZCI_ACCESS_REQUEST_SUBMITTED_INFO_ONLY;

    adobeTrackActionLocationLimited: CtaCallUsLinkLocations =
        CtaCallUsLinkLocations.ZCI_ACCESS_REQUEST_SUBMITTED_LIMITED;

    adobeCTALocationNewUserInfoOnly: CtaCallUsLinkLocations =
        CtaCallUsLinkLocations.ZCI_GRANTOR_UNIQUE_KEY_SUCCESS_INFO;

    adobeCTALocationNewUserLimited: CtaCallUsLinkLocations =
        CtaCallUsLinkLocations.ZCI_GRANTOR_UNIQUE_KEY_SUCCESS_LIMITED;

    adobeLocationData: CtaCallUsLinkLocations;

    @Input() nextStepClicked: EventEmitter<any>;
    @Input() nextStep: EventEmitter<any>;
    @Input() uniquePin: string;

    accountsLabel: string;

    ngOnInit() {
        this.subs.add(this.getAccessType());
        this.trackPageLoad();
        this.selectedUser$ = this.store.select(selectAddUserStepperSelectedUser);
        this.accountBalancesUrl = this.composeAccountBalancesUrl();
        this.adobeLocationData = this.getAdobeCTALocation();
        this.subs.add(
            this.store.select(selectAddUserStepperSelectedAccounts).pipe(take(1)).subscribe((accounts) => {
                this.accountsLabel = accounts?.length > 1 ? 'accounts' : 'account';
            }),
        );
    }

    trackPageLoad() {
        const userTypeAccessTypePageNameMap = {
            [this.newUser$]: {
                [AccessTypeSelection.INFORMATION_ONLY]:
                    AdobePageName.GRANTOR_UNIQUE_KEY_SUCCESS_INFO,
                [AccessTypeSelection.LIMITED_ACCESS]:
                    AdobePageName.GRANTOR_UNIQUE_KEY_SUCCESS_LIMITED,
            },
            [this.existingUser$]: {
                [AccessTypeSelection.INFORMATION_ONLY]:
                    AdobePageName.GAF_ACCESS_REQUEST_SUBMITTED_INFO_ONLY,
                [AccessTypeSelection.LIMITED_ACCESS]:
                    AdobePageName.GAF_ACCESS_REQUEST_SUBMITTED_LIMITED,
            },
        };

        this.selectedUserType$.pipe(take(1)).subscribe((userType) => {
            const adobePageName = userTypeAccessTypePageNameMap[userType]?.[this.accessType];
            if (adobePageName) {
                this.adobeLaunchService.pageLoad(adobePageName);
            }
        });
    }

    getAccessType(): Subscription {
        return this.selectedAccess$.pipe(take(1)).subscribe((accessType) => {
            this.accessType = accessType;
        });
    }

    getAdobeCTALocation(): CtaCallUsLinkLocations {
        this.selectedUserType$
            .pipe(
                take(1),
                map((userType) => {
                    if (userType === this.existingUser$) {
                        return this.accessType === AccessTypeSelection.INFORMATION_ONLY
                            ? this.adobeTrackActionLocationInfoOnly
                            : this.adobeTrackActionLocationLimited;
                    } else {
                        return this.accessType === AccessTypeSelection.INFORMATION_ONLY
                            ? this.adobeCTALocationNewUserInfoOnly
                            : this.adobeCTALocationNewUserLimited;
                    }
                }),
            )
            .subscribe((location) => {
                this.adobeLocationData = location;
            });
        return this.adobeLocationData;
    }

    get fullName$(): Observable<string> {
        return this.selectedUser$.pipe(
            map((account) => `${account?.firstName} ${account?.middleName} ${account?.lastName}`),
        );
    }

    get firstName$(): Observable<string> {
        return this.selectedUser$.pipe(map((account) => `${account?.firstName}`));
    }

    goBackToTriage() {
        const callToActionData: AdobeAnalyticsCallToAction = {
            location: this.adobeLocationData,
            ctaName: AdobeAnalyticsProcessStep.GAF_ADD_ANOTHER_PERSON,
            ctaType: AnalyticsCtaType.BUTTON,
        };
        this.adobeLaunchService.trackAction(callToActionData);
        this.adobeLaunchService.trackProcess(AdobeAnalyticsEvent.PROCESS_STEP_SUCCESS, {
            processType: AdobeAnalyticsProcessType.GAF_GRANT_ACCESS_FLOW,
        });

        this.resetGrantRevokePermissionState();
    }

    composeAccountBalancesUrl(): string {
        const accountBalancesUrl = environment.ACCOUNT_BALANCES_URL;

        if (environment.isProduction) {
            return accountBalancesUrl;
        }

        const nonProdPortNumber =
            this.zciCookieService.nonProdPortNumber || CONSTANTS.DEFAULT_NON_PROD_PORT;

        return accountBalancesUrl.replace('port', nonProdPortNumber);
    }

    resetGrantRevokePermissionState(): void {
        this.store.dispatch(resetGrantRevokePermissionState());
    }

    returnToAccessPermissions(): void {
        this.router.navigate([APPLICATION_ROUTES.OWN_ACCOUNTS]);
    }

    tagGoToMyAccountsButtonInfoOnly(): void { }

    tagGoToMyAccountsButtonLimited(): void { }

    tagGoToMyAccountsButtonFullAuthority(): void { }

    tagMyAccountsAccessLevel() {
        if (this.accessType === AccessTypeSelection.INFORMATION_ONLY) {
            this.tagGoToMyAccountsButtonInfoOnly();
        }
        if (this.accessType === AccessTypeSelection.LIMITED_ACCESS) {
            this.tagGoToMyAccountsButtonLimited();
        }
        if (this.accessType === AccessTypeSelection.FULL_AUTHORITY) {
            this.tagGoToMyAccountsButtonFullAuthority();
        }
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.resetGrantRevokePermissionState();
    }

    protected readonly UserTypeSelection = UserTypeSelection;
}
